/**
 * /zh/from-meeting-notes-to-decisions — 会议记录转决策的中文 pivot 页。
 * 与 FromMeetingNotesToDecisions.tsx 同构，原生中文，hreflang 双向。
 * 不假装是转录/记录工具——「留着你的记录工具」，从它结束处接手。
 */
import { MarketingShell } from './MarketingShell';

export function FromMeetingNotesToDecisionsZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/from-meeting-notes-to-decisions"
      documentTitle="从会议记录到产品决策 · Draftlize"
      documentDescription="AI 会议记录捕住了对话。但里面的决策被埋没、会过时。怎么把原始会议记录变成团队和 AI agent 都能用的「活」的产品上下文。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 会议记录</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.6vw, 56px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            从会议记录，<br/>
            <em>到一直活着的产品决策。</em>
          </h1>
          <p className="lede">
            AI 记录工具——Granola、Otter、Fireflies、Read、Notion AI——把一件事做得很好：捕住说了什么。一通电话结束，一份工整的转录和摘要就出现了。这确实有用，你也该继续用。但会议摘要是<em>原始输入</em>，不是产出。那场会真正要紧的东西——你达成的决策、以及为什么——被埋在一墙记录里，落进一份没人重开的文档，几周内就过时。这页讲的是记录工具<em>之后</em>的那一步：把会议记录里的决策，变成一直保持最新的产品上下文。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">记录工具把一切都捕住了。<em>问题就在这。</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            一份好的会议摘要是全面的——这意味着「这场会之所以存在」的那一个决策，正坐在第九段里，和闲聊、以及关于午饭的跑题被一样地对待。捕捉不再是瓶颈了，<em>提取</em>才是。一场会有价值的残留物很小：几个决策、它们背后的理由、以及谁负责跟进。其余都是你再也不会读第二遍的上下文。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            而且哪怕真有人把决策拎出来，它也落进一份记录文档——扁平文字，和它影响的 spec、它推翻的更早决策都脱了钩。三周后产品往前走了，那个决策悄悄过时，下一场会又把你早已敲定的事重新吵一遍——因为没人找得到「定了什么」、也说不出它是否还成立。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">原始输入 vs 活的上下文</span>
            <h2 className="serif">记录工具到此为止，<em>工作从这开始。</em></h2>
          </div>
          <p>你的记录工具和 Draftlize 做的是不同的活。一个捕住对话；另一个让对话里的决策活着。两个都留着。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>AI 记录工具</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>职责</td><td>捕住说了什么</td><td className="ours yes">让定下的东西保持最新</td></tr>
            <tr><td>产出</td><td className="partial">转录 + 摘要</td><td className="ours yes">图里的决策卡片</td></tr>
            <tr><td>一个决策推翻了更早的</td><td className="no">两条不相关的记录</td><td className="ours yes">相连；旧的标 stale</td></tr>
            <tr><td>三周后</td><td className="no">埋在一份文档里</td><td className="ours yes">可寻址，最新或被标记</td></tr>
            <tr><td>AI agent 能用它</td><td className="partial">读扁平文字</td><td className="ours yes">经 MCP 读写</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">把决策变成<em>活的上下文。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            捕捉那一步，留给你的记录工具。关键在于记录里那几个决策接下来怎么样——在 Draftlize 里它们不再是摘要里的几行，而成为一张一直成立的图。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">决策成为卡片，不是几行</h3>
            <p>把会议摘要贴进 Draftlize，里面的决策就成为结构化卡片——决策、理由、权衡过的备选、负责人。一小时的对话坍缩成「真正必须活下来」的那几样，每个都可按 ID 寻址，而不是埋在第九段。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">新决策去标记它推翻的那个</h3>
            <p>这周的会改了你上个月做的一个决定。因为决策相连，新的那个自动把旧的——以及建立在它上面的每份 spec——标成 stale。下一场会不再重吵已敲定的事，因为「定了什么、是否还成立」就在那儿。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 AI agent 读到「定了什么」</h3>
            <p>会议里的决策成为 Claude Code 或 Cursor 经 MCP 读到的上下文——于是给你起草下一份 spec 的 agent，已经知道团队周二定了什么、为什么，而不是每次都得重读一遍转录。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          你的记录工具把会议捕得完美。它只是说不出：三周后，里面那些决策哪些还成立。
        </blockquote>
        <cite>留着记录工具，让 Draftlize 把它产出的决策保持活着。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'Draftlize 会录音或转录会议吗？', a: '不会——我们也不会假装会。Granola、Otter、Fireflies、Read、Notion AI 这类工具在录音和转录上很出色；继续用你喜欢的那个。Draftlize 在「记录已存在」之后接手：把里面的决策变成一直保持最新的结构化卡片。' },
            { q: '把会议记录就放在文档里有什么不对？', a: '作为对话的存档，没什么不对。问题在于决策——那个要紧的部分——以扁平文字躺着，和它影响的 spec、它推翻的更早决策都脱了钩。于是它们悄悄过时，下一场会又把你早已敲定的事重新决定一遍。' },
            { q: '怎么把会议记录里的决策弄进 Draftlize？', a: '把记录工具产出的摘要贴进来，把里面的决策捕成卡片——决策、理由、负责人。重点不是存整份转录，而是让那几个「必须活得比会议久」的决策保持可寻址、保持最新。' },
            { q: '这和带「待办事项」的 AI 会议记录工具有何不同？', a: '待办事项是会被勾掉的任务；决策不同——它有理由、有依赖、有寿命，且在上游有东西变化时会过时。Draftlize 追踪这个：当后来的决策推翻更早的，依赖它的会标 stale，这是一张待办清单不会做的。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">把会议记录变成决策<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            留着你的记录工具。把摘要丢进 Draftlize，让里面的决策成为活的卡片——相连、最新、AI agent 可读——这样下一场会建立在「你定下的东西」上，而不是把它重新定一遍。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
